import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './add-to-library-panel.css';
import { toasterMsg } from 'Components/Toaster/Toaster';
import noImage from '../../../../assets/images/no-image.png';
import {
  listControlLibraries,
  listLibraryCards,
  createControlLibrary,
  updateControlLibrary,
  createLibraryCard,
  updateLibraryCard,
} from 'services/control-library.services';
import { uploadCMImageS3 } from 'services/custom-module.services';
import Loader from 'Components/Utility/Loader/Loader';
import type { OptionType } from 'Components/Controls/bx-select.types';
import { AddIcon, CloseIcon, DeleteIcon, UploadIcon } from '../../../../bombiix-icon';
import { NewSelectMultiSelect } from '../../../../Components/PIM/common/NewSelectMultiSelect';
import { NewCheckbox } from '../../../../Components/PIM/common/NewCheckbox';
import { NewInput } from '../../../../Components/PIM/common/NewInput';

interface ExistingLibraryFull {
  _id: string;
  libraryName: string;
  iconUrl?: string;
}

interface ExistingCard {
  _id: string;
  cardName: string;
  tableControlIds: string[];
}

interface NewInlineCard {
  tempId: string;
  name: string;
}

interface ExistingLibrarySelection {
  library: ExistingLibraryFull;
  libraryNameEdited: string;
  iconUrl: string;
  iconPreviewUrl: string;
  iconUploading: boolean;
  cards: ExistingCard[];
  cardsLoading: boolean;
  selectedCardIds: Record<string, boolean>;
  newCards: NewInlineCard[];
}

interface NewLibraryForm {
  tempId: string;
  libraryName: string;
  iconPreviewUrl: string;
  iconS3Url: string;
  iconUploading: boolean;
  newCards: NewInlineCard[];
}

interface AddToLibraryPanelProps {
  selectedControlIds: string[];
  onClose: () => void;
  onSuccess: () => void;
}

const makeTempId = () => `t_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const emptyNewLibForm = (): NewLibraryForm => ({
  tempId: makeTempId(),
  libraryName: '',
  iconPreviewUrl: '',
  iconS3Url: '',
  iconUploading: false,
  newCards: [{ tempId: makeTempId(), name: '' }],
});

const AddToLibraryPanel: React.FC<AddToLibraryPanelProps> = ({ selectedControlIds, onClose, onSuccess }) => {
  const { t } = useTranslation('control-library');
  const [saving, setSaving] = useState(false);
  const [allLibraries, setAllLibraries] = useState<ExistingLibraryFull[]>([]);
  const [loadingLibraries, setLoadingLibraries] = useState(true);
  const [selectedLibIds, setSelectedLibIds] = useState<string[]>([]);
  const [existingSelections, setExistingSelections] = useState<Record<string, ExistingLibrarySelection>>({});
  const [newLibForms, setNewLibForms] = useState<NewLibraryForm[]>([]);

  useEffect(() => {
    listControlLibraries({ pageSize: 200 })
      .then((res) => setAllLibraries(res.data?.records || []))
      .catch(() => {})
      .finally(() => setLoadingLibraries(false));
  }, []);

  /* ── Upload icon to S3, returns s3 URL ── */
  const uploadIconToS3 = useCallback(async (file: File): Promise<string> => {
    const base64Image = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    const ext = file.name.split('.').pop() ?? 'png';
    const documentName = `library/icons/${Date.now()}_${Math.random().toString(36).slice(2, 7)}.${ext}`;
    const res = (await uploadCMImageS3({ document: base64Image, documentName })) as { code?: number; data?: string };
    return res?.code === 200 && res?.data ? (res.data as string) : '';
  }, []);

  /* ── Library chip select/deselect ── */
  const handleLibraryChange = useCallback(
    (options: OptionType[]) => {
      const newIds = (options || []).map((o) => String(o.value));
      setSelectedLibIds(newIds);
      setExistingSelections((prev) => {
        const next: Record<string, ExistingLibrarySelection> = {};
        newIds.forEach((libId) => {
          const lib = allLibraries.find((l) => l._id === libId);
          if (!lib) return;
          if (prev[libId]) {
            next[libId] = prev[libId];
            return;
          }
          const iconUrl = lib.iconUrl || '';
          next[libId] = {
            library: lib,
            libraryNameEdited: lib.libraryName,
            iconUrl,
            iconPreviewUrl: iconUrl,
            iconUploading: false,
            cards: [],
            cardsLoading: true,
            selectedCardIds: {},
            newCards: [],
          };
          listLibraryCards(libId, { pageSize: 200 })
            .then((res) => {
              setExistingSelections((s) => ({
                ...s,
                [libId]: { ...s[libId], cards: res.data?.records || [], cardsLoading: false } as ExistingLibrarySelection,
              }));
            })
            .catch(() => {
              setExistingSelections((s) => ({
                ...s,
                [libId]: { ...s[libId], cardsLoading: false } as ExistingLibrarySelection,
              }));
            });
        });
        return next;
      });
    },
    [allLibraries],
  );

  /* ── Existing lib: edit name ── */
  const handleExistingLibNameChange = useCallback((libId: string, name: string) => {
    setExistingSelections((prev) => ({
      ...prev,
      [libId]: { ...prev[libId], libraryNameEdited: name } as ExistingLibrarySelection,
    }));
  }, []);

  /* ── Existing lib: icon upload ── */
  const handleExistingIconUpload = useCallback(
    async (libId: string, file: File) => {
      const previewUrl = URL.createObjectURL(file);
      setExistingSelections((prev) => ({
        ...prev,
        [libId]: { ...prev[libId], iconPreviewUrl: previewUrl, iconUploading: true } as ExistingLibrarySelection,
      }));
      try {
        const s3Url = await uploadIconToS3(file);
        setExistingSelections((prev) => ({
          ...prev,
          [libId]: { ...prev[libId], iconUrl: s3Url, iconUploading: false } as ExistingLibrarySelection,
        }));
      } catch {
        setExistingSelections((prev) => ({
          ...prev,
          [libId]: { ...prev[libId], iconUploading: false } as ExistingLibrarySelection,
        }));
        toasterMsg(t('ICON_UPLOAD_FAILED'), 'danger');
      }
    },
    [uploadIconToS3, t],
  );

  /* ── Existing lib: remove icon ── */
  const handleExistingIconRemove = useCallback((libId: string) => {
    setExistingSelections((prev) => ({
      ...prev,
      [libId]: { ...prev[libId], iconUrl: '', iconPreviewUrl: '' } as ExistingLibrarySelection,
    }));
  }, []);

  /* ── Existing lib: toggle card checkbox ── */
  const toggleExistingCard = useCallback((libId: string, cardId: string) => {
    setExistingSelections((prev) => {
      const sel = prev[libId];
      if (!sel) return prev;
      return {
        ...prev,
        [libId]: {
          ...sel,
          selectedCardIds: { ...sel.selectedCardIds, [cardId]: !sel.selectedCardIds[cardId] },
        },
      };
    });
  }, []);

  /* ── Existing lib: add inline new card (auto-checked) ── */
  const handleAddNewCard = useCallback((libId: string) => {
    setExistingSelections((prev) => {
      const sel = prev[libId];
      if (!sel) return prev;
      return {
        ...prev,
        [libId]: { ...sel, newCards: [...sel.newCards, { tempId: makeTempId(), name: '' }] },
      };
    });
  }, []);

  /* ── Existing lib: update inline new card name ── */
  const handleNewCardNameChange = useCallback((libId: string, tempId: string, name: string) => {
    setExistingSelections((prev) => {
      const sel = prev[libId];
      if (!sel) return prev;
      return {
        ...prev,
        [libId]: { ...sel, newCards: sel.newCards.map((c) => (c.tempId === tempId ? { ...c, name } : c)) },
      };
    });
  }, []);

  /* ── New library form: add ── */
  const handleAddNewLibForm = useCallback(() => {
    setNewLibForms((prev) => [...prev, emptyNewLibForm()]);
  }, []);

  /* ── New library form: remove ── */
  const handleRemoveNewLibForm = useCallback((tempId: string) => {
    setNewLibForms((prev) => prev.filter((f) => f.tempId !== tempId));
  }, []);

  /* ── New library form: edit name ── */
  const handleNewLibNameChange = useCallback((tempId: string, name: string) => {
    setNewLibForms((prev) => prev.map((f) => (f.tempId === tempId ? { ...f, libraryName: name } : f)));
  }, []);

  /* ── New library form: add inline card ── */
  const handleNewLibAddCard = useCallback((tempId: string) => {
    setNewLibForms((prev) => prev.map((f) => (f.tempId === tempId ? { ...f, newCards: [...f.newCards, { tempId: makeTempId(), name: '' }] } : f)));
  }, []);

  /* ── New library form: edit card name ── */
  const handleNewLibCardNameChange = useCallback((libTempId: string, cardTempId: string, name: string) => {
    setNewLibForms((prev) =>
      prev.map((f) => (f.tempId === libTempId ? { ...f, newCards: f.newCards.map((c) => (c.tempId === cardTempId ? { ...c, name } : c)) } : f)),
    );
  }, []);

  /* ── New library form: icon upload ── */
  const handleNewLibIconUpload = useCallback(
    async (tempId: string, file: File) => {
      const previewUrl = URL.createObjectURL(file);
      setNewLibForms((prev) => prev.map((f) => (f.tempId === tempId ? { ...f, iconPreviewUrl: previewUrl, iconUploading: true } : f)));
      try {
        const s3Url = await uploadIconToS3(file);
        setNewLibForms((prev) => prev.map((f) => (f.tempId === tempId ? { ...f, iconS3Url: s3Url, iconUploading: false } : f)));
      } catch {
        setNewLibForms((prev) => prev.map((f) => (f.tempId === tempId ? { ...f, iconUploading: false } : f)));
        toasterMsg(t('ICON_UPLOAD_FAILED'), 'danger');
      }
    },
    [uploadIconToS3, t],
  );

  /* ── New library form: remove icon ── */
  const handleNewLibIconRemove = useCallback((tempId: string) => {
    setNewLibForms((prev) => prev.map((f) => (f.tempId === tempId ? { ...f, iconPreviewUrl: '', iconS3Url: '' } : f)));
  }, []);

  /* ── Save ── */
  const handleSave = useCallback(async () => {
    for (const sel of Object.values(existingSelections)) {
      if (sel.iconUploading) {
        toasterMsg(t('ICON_UPLOADING'), 'warning');
        return;
      }
      const hasNewCards = sel.newCards.some((c) => c.name.trim());
      const hasCheckedCards = Object.values(sel.selectedCardIds).some(Boolean);
      if (!hasNewCards && !hasCheckedCards) {
        toasterMsg(t('CARD_ID_REQUIRED'), 'danger');
        return;
      }
    }
    for (const form of newLibForms) {
      if (form.iconUploading) {
        toasterMsg(t('ICON_UPLOADING'), 'warning');
        return;
      }
      if (!form.libraryName.trim()) {
        toasterMsg(t('NAME_REQUIRED'), 'danger');
        return;
      }
      if (!form.newCards.some((c) => c.name.trim())) {
        toasterMsg(t('CARD_NAME_REQUIRED'), 'danger');
        return;
      }
    }

    setSaving(true);
    try {
      for (const sel of Object.values(existingSelections)) {
        const nameChanged = sel.libraryNameEdited.trim() !== sel.library.libraryName;
        const iconChanged = sel.iconUrl !== (sel.library.iconUrl || '');
        if (nameChanged || iconChanged) {
          await updateControlLibrary(sel.library._id, {
            libraryName: sel.libraryNameEdited.trim() || sel.library.libraryName,
            iconUrl: sel.iconUrl,
          });
        }
        for (const nc of sel.newCards.filter((c) => c.name.trim())) {
          await createLibraryCard(sel.library._id, { cardName: nc.name.trim(), tableControlIds: selectedControlIds });
        }
        const checkedIds = Object.keys(sel.selectedCardIds).filter((id) => sel.selectedCardIds[id]);
        for (const cardId of checkedIds) {
          const card = sel.cards.find((c) => c._id === cardId);
          if (card) {
            const merged = [...new Set([...card.tableControlIds.map(String), ...selectedControlIds])];
            await updateLibraryCard(sel.library._id, cardId, { tableControlIds: merged });
          }
        }
      }

      for (const form of newLibForms) {
        const libRes = await createControlLibrary({ libraryName: form.libraryName.trim(), iconUrl: form.iconS3Url });
        const newLibId = libRes?.data?._id;
        if (newLibId) {
          for (const nc of form.newCards.filter((c) => c.name.trim())) {
            await createLibraryCard(newLibId, { cardName: nc.name.trim(), tableControlIds: selectedControlIds });
          }
        }
      }

      toasterMsg(t('SAVE_SUCCESS'), 'success');
      onSuccess();
      onClose();
    } catch {
      toasterMsg(t('SAVE_FAILED'), 'danger');
    } finally {
      setSaving(false);
    }
  }, [existingSelections, newLibForms, selectedControlIds, onSuccess, onClose, t]);

  const hasAnything = selectedLibIds.length > 0 || newLibForms.length > 0;

  /* ── Icon row ── */
  const renderIconRow = (previewUrl: string, onFileChange: (file: File) => void, onRemove: () => void, uploading: boolean) => (
    <div className="atl-icon-wrap">
      <img src={previewUrl || noImage} alt="library icon" className="atl-icon-img" />
      <label className="btn p-0 cursor-pointer mb-0 atl-icon-upload-btn" title={t('LIBRARY_ICON')}>
        {uploading ? <span className="spinner-border spinner-border-sm" /> : <UploadIcon size={24} />}
        <input
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
          className="atl-icon-file-input"
          onChange={(e) => {
            const selectedFile = e.target.files?.[0];
            if (selectedFile) onFileChange(selectedFile);
            e.target.value = '';
          }}
        />
      </label>
      {previewUrl && !uploading && (
        <button type="button" className="btn p-0 text-danger atl-icon-delete-btn" onClick={onRemove} title={t('DELETE')}>
          <DeleteIcon size={24} className="text-danger" />
        </button>
      )}
    </div>
  );

  /* ── Card list section — shared for existing and new library forms ── */
  const renderCardList = (
    existingCards: ExistingCard[],
    cardsLoading: boolean,
    selectedCardIds: Record<string, boolean>,
    onToggle: (cardId: string) => void,
    newCards: NewInlineCard[],
    onNewCardNameChange: (tempId: string, name: string) => void,
    onAddCard: () => void,
    showAddCard = false,
  ) => (
    <div>
      <p className="atl-card-list-label">{t('LIBRARY_CARD_NAME')}</p>
      {cardsLoading ? (
        <Loader />
      ) : (
        <div className="atl-card-list-column">
          {existingCards.map((card) => (
            <div key={card._id} className="atl-card-row">
              <NewCheckbox id={`card-${card._id}`} checked={!!selectedCardIds[card._id]} onChange={() => onToggle(card._id)} className="atl-card-checkbox" />
              <label htmlFor={`card-${card._id}`} className="atl-card-label">
                {card.cardName}
              </label>
            </div>
          ))}

          {newCards.map((nc) => (
            <div key={nc.tempId} className="atl-card-row">
              <NewCheckbox checked={true} onChange={() => {}} className="atl-card-checkbox" />
              <div className="atl-card-name-input">
                <NewInput
                  type="text"
                  value={nc.name}
                  onChange={(e) => onNewCardNameChange(nc.tempId, e.target.value)}
                  placeholder={t('ENTER_CARD_NAME')}
                  autoFocus
                />
              </div>
            </div>
          ))}

          {showAddCard && (
            <div className="add-card-footer cursor-pointer" onClick={onAddCard}>
              <span className="atl-add-card-text mt-1">{t('ADD_NEW_CARD')}</span>
              <button type="button" className="btn p-0 d-flex align-items-center justify-content-center atl-add-card-btn">
                <AddIcon size={24} className="text-white" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="offcanvas offcanvas-end show atl-panel-visible" tabIndex={-1} role="dialog" aria-labelledby="addToLibraryTitle">
      <div className="offcanvas-header">
        <div className="d-inline-flex align-items-center">
          <button className="bombiix-btn btn-blue" type="button" onClick={handleSave} disabled={saving || !hasAnything}>
            {saving ? `${t('SAVE')}...` : t('SAVE')}
          </button>
        </div>
        <h4 className="mx-auto my-0 text-bold" id="addToLibraryTitle">
          {t('ADD_TO_LIBRARY')}
        </h4>
        <CloseIcon size={24} className="cursor-pointer" onClick={onClose} aria-label="Close" />
      </div>

      <div className="offcanvas-body">
        {/* Multi-select existing libraries (renders as chips) */}
        <div className="mb-4">
          {loadingLibraries ? (
            <Loader />
          ) : (
            <NewSelectMultiSelect
              menuPortalTarget={document.body}
              menuPosition="fixed"
              isClearable={false}
              options={allLibraries.map((lib) => ({ value: lib._id, label: lib.libraryName }))}
              value={
                selectedLibIds
                  .map((id) => {
                    const lib = allLibraries.find((l) => l._id === id);
                    return lib ? { value: lib._id, label: lib.libraryName } : null;
                  })
                  .filter(Boolean) as OptionType[]
              }
              onChange={handleLibraryChange}
              placeholder={t('SELECT_EXISTING_LIBRARIES')}
            />
          )}
        </div>

        {/* One section per selected existing library */}
        {selectedLibIds.map((libId) => {
          const sel = existingSelections[libId];
          if (!sel) return null;
          return (
            <div key={libId} className="mb-4 pb-4 atl-lib-section">
              {renderIconRow(
                sel.iconPreviewUrl,
                (file) => handleExistingIconUpload(libId, file),
                () => handleExistingIconRemove(libId),
                sel.iconUploading,
              )}
              <div className="mb-3">
                <p className="f-600 m-0 mb-1">{t('LIBRARY_NAME')}</p>
                <NewInput
                  type="text"
                  value={sel.libraryNameEdited}
                  onChange={(e) => handleExistingLibNameChange(libId, e.target.value)}
                  placeholder={t('ENTER_LIBRARY_NAME')}
                />
              </div>
              {renderCardList(
                sel.cards,
                sel.cardsLoading,
                sel.selectedCardIds,
                (cardId) => toggleExistingCard(libId, cardId),
                sel.newCards,
                (tempId, name) => handleNewCardNameChange(libId, tempId, name),
                () => handleAddNewCard(libId),
                true,
              )}
            </div>
          );
        })}

        {/* One section per new library form */}
        {newLibForms.map((form) => (
          <div key={form.tempId} className="mb-4 pb-4 atl-lib-section">
            <div className="d-flex justify-content-end mb-1">
              <span title={t('DELETE')} className="cursor-pointer" onClick={() => handleRemoveNewLibForm(form.tempId)}><DeleteIcon size={24} className="text-danger" /></span>
            </div>
            {renderIconRow(
              form.iconPreviewUrl,
              (file) => handleNewLibIconUpload(form.tempId, file),
              () => handleNewLibIconRemove(form.tempId),
              form.iconUploading,
            )}
            <div className="mb-3">
              <p className="f-600 m-0 mb-1">{t('LIBRARY_NAME')}</p>
              <NewInput
                type="text"
                value={form.libraryName}
                onChange={(e) => handleNewLibNameChange(form.tempId, e.target.value)}
                placeholder={t('ENTER_LIBRARY_NAME')}
              />
            </div>
            {renderCardList(
              [],
              false,
              {},
              () => {},
              form.newCards,
              (cardTempId, name) => handleNewLibCardNameChange(form.tempId, cardTempId, name),
              () => handleNewLibAddCard(form.tempId),
            )}
          </div>
        ))}

        {/* Create Library — adds a new blank library form */}
        <div className="mt-3 d-flex justify-content-center">
          <button type="button" className="bombiix-btn btn-blue" onClick={handleAddNewLibForm}>
            {t('CREATE_LIBRARY')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddToLibraryPanel;
